/**
 * Inside the router: mirrors the signed-in user's update-notices preference
 * onto <html data-update-notices> for AppUpdateNotice (mounted above the
 * router), and moves the tab onto a waiting release as the user changes page,
 * unless the page they leave or open is an editor, or work is unsaved. The URL has
 * already changed by then, so a reload lands them where they were going, on
 * the new build. See lib/appUpdates.ts.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  getUpdateState, hasUnsavedWork, readSwitchAttempt, recordSwitchAttempt, shouldSwitchOnNavigate,
  UPDATE_NOTICE_MODE_ATTRIBUTE,
} from '@/lib/appUpdates';
import { useUpdateNotices } from './useUpdateNotices';

export function AppUpdateSync() {
  const { user } = useAuth();
  const { mode } = useUpdateNotices();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.documentElement;
    if (user) el.setAttribute(UPDATE_NOTICE_MODE_ATTRIBUTE, mode);
    else el.removeAttribute(UPDATE_NOTICE_MODE_ATTRIBUTE);
  }, [user, mode]);

  const { pathname } = useLocation();
  const previous = useRef(pathname);
  useEffect(() => {
    const from = previous.current;
    previous.current = pathname;
    if (from === pathname) return;
    // The page being left has already unmounted its guard, so ask about the
    // page we came from by path; unsaved work registered by the new page
    // (rare on first render) still blocks.
    const state = getUpdateState();
    if (!state.build || !shouldSwitchOnNavigate(state, from, hasUnsavedWork(), readSwitchAttempt(), pathname)) return;
    recordSwitchAttempt(state.build);
    window.location.reload();
  }, [pathname]);
  return null;
}
